<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import TableHeaderCell from 'dashboard/components/widgets/TableHeaderCell.vue';
import CheckBox from 'v3/components/Form/CheckBox.vue';
import {
  hasPushPermissions,
  requestPushPermissions,
  verifyServiceWorkerExistence,
} from 'dashboard/helper/pushHelper.js';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { NOTIFICATION_CATEGORIES } from 'dashboard/helper/notificationCategories';
import { NOTIFICATION_TYPES } from './constants';

export default {
  components: { TableHeaderCell, ToggleSwitch, CheckBox },
  data() {
    return {
      selectedEmailFlags: [],
      selectedPushFlags: [],
      enableAudioAlerts: false,
      hasEnabledPushPermissions: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      emailFlags: 'userNotificationSettings/getSelectedEmailFlags',
      pushFlags: 'userNotificationSettings/getSelectedPushFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentRole: 'getCurrentRole',
    }),
    hasPushAPISupport() {
      return !!('Notification' in window);
    },
    isAdmin() {
      return this.currentRole === 'administrator';
    },
    isSLAEnabled() {
      return this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.SLA);
    },
    isVoipEnabled() {
      return !!(
        this.isFeatureEnabledonAccount(this.accountId, 'voip_calls') ||
        this.isFeatureEnabledonAccount(this.accountId, FEATURE_FLAGS.VOIP_CALLS)
      );
    },
    filteredNotificationTypes() {
      return NOTIFICATION_TYPES.filter(notification => {
        if (notification.adminOnly && !this.isAdmin) return false;
        if (notification.requiresSla && !this.isSLAEnabled) return false;
        if (notification.requiresVoip && !this.isVoipEnabled) return false;
        return true;
      });
    },
    groupedNotificationTypes() {
      const byValue = Object.fromEntries(
        this.filteredNotificationTypes.map(notification => [
          notification.value,
          notification,
        ])
      );

      return NOTIFICATION_CATEGORIES.map(category => ({
        key: category.key,
        icon: category.icon,
        items: category.types.map(type => byValue[type]).filter(Boolean),
      })).filter(group => group.items.length > 0);
    },
    emailNotificationTypes() {
      return this.filteredNotificationTypes.filter(
        notification => notification.emailSupported !== false
      );
    },
    allPushEnabled() {
      return (
        this.filteredNotificationTypes.length > 0 &&
        this.filteredNotificationTypes.every(notification =>
          this.selectedPushFlags.includes(`push_${notification.value}`)
        )
      );
    },
  },
  watch: {
    emailFlags(value) {
      this.selectedEmailFlags = value;
    },
    pushFlags(value) {
      this.selectedPushFlags = value;
    },
  },
  mounted() {
    if (hasPushPermissions()) this.getPushSubscription();
    this.$store.dispatch('userNotificationSettings/get');
  },
  methods: {
    supportsEmail(notification) {
      return notification.emailSupported !== false;
    },
    typeLabel(notification) {
      return notification.label.startsWith('PROFILE_')
        ? this.$t(notification.label)
        : notification.label;
    },
    categoryLabel(key) {
      return this.$t(`INBOX.CATEGORIES.${key.toUpperCase()}`);
    },
    checkFlagStatus(type, flagType) {
      const selectedFlags =
        type === 'email' ? this.selectedEmailFlags : this.selectedPushFlags;
      return selectedFlags.includes(`${type}_${flagType}`);
    },
    onRegistrationSuccess() {
      this.hasEnabledPushPermissions = true;
    },
    onRequestPermissions(value) {
      if (value) {
        requestPushPermissions({ onSuccess: this.onRegistrationSuccess });
      } else {
        this.disablePushPermissions();
      }
    },
    disablePushPermissions() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(sub => (sub ? sub.unsubscribe() : null))
          .finally(() => {
            this.hasEnabledPushPermissions = false;
          })
          .catch(() => {})
      );
    },
    getPushSubscription() {
      verifyServiceWorkerExistence(registration =>
        registration.pushManager
          .getSubscription()
          .then(sub => {
            this.hasEnabledPushPermissions = !!sub;
          })
          // eslint-disable-next-line no-console
          .catch(error => console.log(error))
      );
    },
    async updateNotificationSettings() {
      try {
        // Sem o await o sucesso saia antes da resposta e o erro nunca caia
        // no catch: a tela dizia "salvo" com o valor que o servidor recusou.
        await this.$store.dispatch('userNotificationSettings/update', {
          selectedEmailFlags: this.selectedEmailFlags,
          selectedPushFlags: this.selectedPushFlags,
        });
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_SUCCESS'));
      } catch (error) {
        // Desfaz o clique: volta ao que o store tem gravado.
        this.selectedEmailFlags = [...this.emailFlags];
        this.selectedPushFlags = [...this.pushFlags];
        useAlert(this.$t('PROFILE_SETTINGS.FORM.API.UPDATE_ERROR'));
      }
    },
    handleInput(type, id) {
      if (type === 'email') this.handleEmailInput(id);
      else this.handlePushInput(id);
    },
    handleEmailInput(id) {
      this.selectedEmailFlags = this.toggleInput(this.selectedEmailFlags, id);
      this.updateNotificationSettings();
    },
    handlePushInput(id) {
      this.selectedPushFlags = this.toggleInput(this.selectedPushFlags, id);
      this.updateNotificationSettings();
    },
    toggleInput(selected, current) {
      if (selected.includes(current))
        return selected.filter(f => f !== current);
      return [...selected, current];
    },
    toggleAllPush(enable) {
      const allKeys = this.filteredNotificationTypes.map(
        notification => `push_${notification.value}`
      );
      if (enable) {
        const next = new Set(this.selectedPushFlags);
        allKeys.forEach(key => next.add(key));
        this.selectedPushFlags = Array.from(next);
      } else {
        this.selectedPushFlags = this.selectedPushFlags.filter(
          flag => !allKeys.includes(flag)
        );
      }
      this.updateNotificationSettings();
    },
  },
};
</script>

<template>
  <div id="profile-settings-notifications" class="flex flex-col gap-4">
    <div
      v-if="!isAdmin"
      class="flex items-start gap-3 p-3 rounded-xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30"
    >
      <span
        class="i-ri-information-line size-4 text-n-blue-11 mt-0.5 flex-shrink-0"
      />
      <div class="flex-1 text-xs">
        <b class="block text-sm mb-0.5 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.AGENT_BANNER_TITLE') }}
        </b>
        <span class="text-n-slate-11">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.AGENT_BANNER_BODY') }}
        </span>
      </div>
    </div>

    <div
      class="flex items-start gap-3 p-3 rounded-xl border border-n-weak bg-n-slate-2"
    >
      <span
        class="i-ri-notification-3-line size-4 text-n-slate-11 mt-0.5 flex-shrink-0"
      />
      <span class="text-xs text-n-slate-11">
        {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.DELIVERY_HINT') }}
      </span>
    </div>

    <div
      class="flex items-center justify-between gap-3 p-3 rounded-xl border border-n-weak bg-n-slate-2"
    >
      <div class="flex flex-col gap-0.5">
        <span class="text-sm font-semibold text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.MASTER_TOGGLE_TITLE') }}
        </span>
        <span class="text-xs text-n-slate-11">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.MASTER_TOGGLE_BODY') }}
        </span>
      </div>
      <ToggleSwitch
        :model-value="allPushEnabled"
        @update:model-value="toggleAllPush"
      />
    </div>

    <div class="hidden sm:block">
      <div
        class="grid content-center h-12 grid-cols-12 gap-4 py-0 rounded-t-xl"
      >
        <TableHeaderCell
          :span="7"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.TYPE_TITLE') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="2"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL')}`"
        >
          <span class="text-heading-3 normal-case text-n-slate-12">
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL') }}
          </span>
        </TableHeaderCell>
        <TableHeaderCell
          :span="3"
          label="`${$t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH')}`"
        >
          <span
            class="text-heading-3 normal-case text-n-slate-12 whitespace-nowrap"
          >
            {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.PUSH') }}
          </span>
        </TableHeaderCell>
      </div>
      <div
        v-for="group in groupedNotificationTypes"
        :key="group.key"
        :data-testid="`notification-group-${group.key}`"
      >
        <div class="flex items-center gap-2 py-3">
          <span class="size-4 text-n-slate-11" :class="group.icon" />
          <span class="text-sm font-semibold text-n-slate-12">
            {{ categoryLabel(group.key) }}
          </span>
        </div>
        <div
          v-for="notification in group.items"
          :key="notification.value"
          class="grid items-start content-center grid-cols-12 gap-4 py-3 border-b border-n-slate-3"
        >
          <div class="flex flex-col gap-1 col-span-7 px-0 text-sm">
            <span class="text-body-main text-n-slate-12 font-medium">
              {{ typeLabel(notification) }}
            </span>
            <span
              v-if="notification.description"
              class="text-xs text-n-slate-11"
            >
              {{ notification.description }}
            </span>
          </div>
          <div class="flex items-start gap-2 px-0 text-sm pt-1 col-span-2">
            <CheckBox
              v-if="supportsEmail(notification)"
              :value="`email_${notification.value}`"
              :is-checked="checkFlagStatus('email', notification.value)"
              @update="id => handleInput('email', id)"
            />
            <span
              v-else
              v-tooltip="
                $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL_UNSUPPORTED')
              "
              class="i-ri-mail-close-line size-4 text-n-slate-8"
              :aria-label="
                $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.EMAIL_UNSUPPORTED')
              "
            />
          </div>
          <div class="flex items-start gap-2 px-0 text-sm pt-1 col-span-3">
            <CheckBox
              :value="`push_${notification.value}`"
              :is-checked="checkFlagStatus('push', notification.value)"
              @update="id => handleInput('push', id)"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-6 sm:hidden">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL_NOTIFICATIONS_SECTION.TITLE') }}
      </span>
      <div class="flex flex-col gap-4">
        <div
          v-for="notification in emailNotificationTypes"
          :key="`email-${notification.value}`"
          class="flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <CheckBox
              :id="`email_${notification.value}`"
              :value="`email_${notification.value}`"
              :is-checked="checkFlagStatus('email', notification.value)"
              @update="handleEmailInput"
            />
            <span class="text-body-main text-n-slate-12">
              {{ typeLabel(notification) }}
            </span>
          </div>
          <span
            v-if="notification.description"
            class="text-xs text-n-slate-11 ml-6"
          >
            {{ notification.description }}
          </span>
        </div>
      </div>

      <div class="flex items-center justify-start gap-2">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.TITLE') }}
        </span>
      </div>
      <div
        v-for="group in groupedNotificationTypes"
        :key="`mobile-${group.key}`"
        class="flex flex-col gap-3"
      >
        <div class="flex items-center gap-2">
          <span class="size-4 text-n-slate-11" :class="group.icon" />
          <span class="text-sm font-semibold text-n-slate-12">
            {{ categoryLabel(group.key) }}
          </span>
        </div>
        <div
          v-for="notification in group.items"
          :key="`push-${notification.value}`"
          class="flex flex-col gap-1"
        >
          <div class="flex items-center gap-2">
            <CheckBox
              :id="`push_${notification.value}`"
              :value="`push_${notification.value}`"
              :is-checked="checkFlagStatus('push', notification.value)"
              @update="handlePushInput"
            />
            <span class="text-body-main text-n-slate-12">
              {{ typeLabel(notification) }}
            </span>
          </div>
          <span
            v-if="notification.description"
            class="text-xs text-n-slate-11 ml-6"
          >
            {{ notification.description }}
          </span>
        </div>
      </div>
    </div>

    <div
      class="flex items-center justify-between w-full gap-2 p-4 border border-solid border-n-weak rounded-2xl"
    >
      <div class="flex flex-row items-center gap-2">
        <span
          class="i-ri-bell-line size-[18px] flex-shrink-0 text-n-slate-12"
        />
        <span class="text-body-main text-n-slate-12">
          {{ $t('PROFILE_SETTINGS.FORM.NOTIFICATIONS.BROWSER_PERMISSION') }}
        </span>
      </div>
      <ToggleSwitch
        v-model="hasEnabledPushPermissions"
        @change="onRequestPermissions"
      />
    </div>
  </div>
</template>
